import { useEffect, useMemo, useRef, useState } from "react";
import { useGlobalQuery } from "../api/useGlobalQuery";
import { useAuth } from "../state/AuthContext";
import { formatLocalDateTime } from "../lib/format";
import { ResponsiveRecordTable } from "./ResponsiveRecordTable";
import { FIRST_CALCULATION_TEXT } from '../lib/definitions/analysis';
import {
  airtimePausedText,
  ALL,
  axisLabelIndexes,
  axisStep,
  busiestObserverText,
  barTickLabel,
  tzAbbrev,
  busiestTip,
  NO_BASELINE,
  noAreasText,
  packetTypeName,
  scopeFilterNote,
  advertAirtimeLine,
  airtimeLegendText,
  airtimeRows,
  AIRTIME_SWITCH_TIP,
  hasAirtimeTypes,
  channelRows,
  chartYMax,
  DASH,
  DEFAULT_THRESHOLDS,
  donutArcPath,
  donutSegments,
  fmtCount,
  fmtDbm,
  fmtNum,
  fmtPct,
  fmtRef,
  filterRegions,
  FLOOD_TOP_SCOPES,
  floodReachView,
  sortFloodObservers,
  NOT_ENOUGH_OBSERVERS_LABEL,
  reachLevel,
  fullSeries,
  hasMoreRows,
  isPending,
  localHour,
  localWeekdayHour,
  noiseAbove,
  noiseLevel,
  NO_CHANNELS_TEXT,
  observerCount,
  notEnoughObserversText,
  packetsLine,
  peakText,
  refLineInfo,
  refLineTip,
  regionTitle,
  repeaterDutyText,
  basisTip,
  RF_WINDOWS,
  sortNoise,
  spillSentence,
  topRows,
  trendView,
  typeLegendText,
  utilBarScale,
  utilLevel,
  type Level,
  type RfFloodScope,
  type RfFloodObserver,
  type RfHealthResponse,
  type RfReferenceLines,
  type RfRegion,
  type RfThresholds,
  type RfTrend,
  type RfUtilPoint,
  type RfWindow,
  type TrendKind,
} from "../lib/rfHealth";
import { AddRegionMenu } from "./RegionManager";
import { HoverTip } from "./HoverTip";

// Analysis > MeshCore > RF Health (replaces the old Airtime tab). One card
// per region, busiest first (the stored payload's own order). Everything the
// page says is decided by lib/rfHealth.ts; this file only lays it out.

const BASIS_TITLE: Record<string, string> = {
  external: "Reported via CoreScope",
};

function BasisBadges({ basis, external }: { basis: "measured" | "estimated"; external?: boolean }) {
  return (
    <>
      <HoverTip text={basisTip(basis)} className="rf-pill-tip">
        <span className="rf-basis">{basis}</span>
      </HoverTip>
      {external && (
        <HoverTip text={BASIS_TITLE.external} className="rf-pill-tip">
          <span className="rf-basis">external</span>
        </HoverTip>
      )}
    </>
  );
}

function NodeLink({ nodeKey, name }: { nodeKey: string; name: string }) {
  return (
    <a href={`#/nodes/${encodeURIComponent(nodeKey)}`} className="node-name-link">
      {name}
    </a>
  );
}

/** A name in a table cell: one line with an ellipsis, the full name in the tooltip. */
function NameCell({ nodeKey, name, linked = true }: { nodeKey: string; name: string; linked?: boolean }) {
  return (
    <HoverTip text={name} className="rf-name-tip">
      <span className="rf-name">{linked ? <NodeLink nodeKey={nodeKey} name={name} /> : name}</span>
    </HoverTip>
  );
}

const LEVEL_VAR: Record<Level, string> = { good: "var(--rf-good)", warn: "var(--rf-warn)", bad: "var(--rf-bad)" };

function TrendTag({ kind, trend, windowKey }: { kind: TrendKind; trend: RfTrend; windowKey: RfWindow }) {
  const t = trendView(kind, trend.current, trend.previous, windowKey, trend.previous_hours, trend.window_hours);
  // Each metric speaks for itself: an arrow where it has a baseline, "no baseline" where it has none.
  if (t.tone === "none" && t.text !== NO_BASELINE) return null;
  return (
    <HoverTip text={t.title} className="rf-trend-tip">
      <span className="rf-trend" data-tone={t.tone}>
        {t.text}
      </span>
    </HoverTip>
  );
}

// --- channel utilization by hour -------------------------------------------

const CHART_H = 170;
const MARGIN = { top: 12, right: 12, bottom: 20, left: 40 };

function UtilByHourChart({
  series,
  refs,
  windowKey,
  th,
}: {
  series: RfUtilPoint[];
  refs: RfReferenceLines;
  windowKey: RfWindow;
  th: RfThresholds;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(320);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Always every hour of the window (24 / 168): an hour with no data is an
  // empty hatched slot, never left out.
  const slots = useMemo(() => fullSeries(series, windowKey), [series, windowKey]);
  const plotW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotH = CHART_H - MARGIN.top - MARGIN.bottom;
  const yMax = chartYMax(slots.map((p) => p.pct), refs.max_backoff);
  const yFor = (v: number) => MARGIN.top + plotH - (Math.min(yMax, Math.max(0, v)) / yMax) * plotH;
  const slot = slots.length > 0 ? plotW / slots.length : plotW;
  const barW = Math.max(1, Math.min(18, slot * 0.72));
  const label = windowKey === "7d" ? localWeekdayHour : localHour;
  const ticks = Array.from({ length: Math.floor(yMax / 10) + 1 }, (_, i) => i * 10);
  // A no-data slot rises only to the top gridline, never above it.
  const topY = yFor(ticks[ticks.length - 1]);
  const lines = refLineInfo(refs).map((l) => ({ ...l, text: `${l.name} ${fmtRef(l.value)}%` }));

  if (slots.length === 0) return <div className="telemetry-chart-empty">No hours in this window.</div>;

  const xLabels = axisLabelIndexes(slots.length, axisStep(windowKey));

  return (
    <div className="telemetry-chart-svg-wrap rf-chart-wrap" ref={wrapRef}>
      <svg className="telemetry-chart-svg" width={width} height={CHART_H} role="img" aria-label="Channel utilization by hour, in percent">
        <defs>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line className="telemetry-chart-gridline" x1={MARGIN.left} y1={yFor(t)} x2={MARGIN.left + plotW} y2={yFor(t)} />
            <text className="rf-chart-tick" x={MARGIN.left - 5} y={yFor(t) + 3} textAnchor="end">
              {t}%
            </text>
          </g>
        ))}
        {slots.map((p, i) => {
          const x = MARGIN.left + i * slot + (slot - barW) / 2;
          if (p.pct === null) {
            return (
              <rect
                key={p.hour_start}
                className="rf-chart-nodata"
                x={x}
                y={topY}
                width={barW}
                height={Math.max(0, MARGIN.top + plotH - topY)}
              />
            );
          }
          const lvl = utilLevel(p.pct, th);
          return (
            <rect
              key={p.hour_start}
              x={x}
              y={yFor(p.pct)}
              width={barW}
              height={Math.max(0, MARGIN.top + plotH - yFor(p.pct))}
              fill={lvl ? LEVEL_VAR[lvl] : "var(--rf-bar)"}
            />
          );
        })}
        {lines.map((l) => (
          <g key={l.text}>
            <line
              x1={MARGIN.left}
              y1={yFor(l.value)}
              x2={MARGIN.left + plotW}
              y2={yFor(l.value)}
              stroke="var(--rf-ref-line)"
              strokeWidth={1}
              strokeDasharray="5 4"
            />
            <rect
              className="rf-chart-ref-bg"
              x={MARGIN.left + plotW - Math.round(l.text.length * 5 + 6)}
              y={yFor(l.value) - 12}
              width={Math.round(l.text.length * 5 + 6)}
              height={11}
              rx={2}
            />
            <text className="rf-chart-ref" x={MARGIN.left + plotW - 3} y={yFor(l.value) - 3} textAnchor="end">
              {l.text}
            </text>
          </g>
        ))}
        {xLabels.map((i) => (
          <text key={slots[i].hour_start} className="rf-chart-tick" x={MARGIN.left + i * slot + slot / 2} y={CHART_H - 4} textAnchor="middle">
            {label(slots[i].hour_start)}
          </text>
        ))}
      </svg>
      {/* Hover targets over the plot (real tooltips; an SVG <title> is the browser's own). */}
      {slots.map((p, i) => (
        <div key={p.hour_start} className="rf-chart-hit" style={{ left: MARGIN.left + i * slot, top: MARGIN.top, width: slot, height: plotH }}>
          <HoverTip block text={p.pct === null ? "No data for this hour" : `${label(p.hour_start)}: ${fmtPct(p.pct)}`}>
            <span className="rf-chart-hit-fill" />
          </HoverTip>
        </div>
      ))}
      {lines.map((l) => {
        const w = Math.round(l.text.length * 5 + 6);
        return (
          <div key={l.text} className="rf-chart-hit" style={{ left: MARGIN.left + plotW - w, top: yFor(l.value) - 13, width: w, height: 12 }}>
            <HoverTip block text={refLineTip(l)}>
              <span className="rf-chart-hit-fill" />
            </HoverTip>
          </div>
        );
      })}
    </div>
  );
}

// --- packet-type donut ---------------------------------------------------------

const DONUT = { size: 132, outer: 62, inner: 38 };

function PacketTypeDonut({ r, windowKey }: { r: RfRegion; windowKey: RfWindow }) {
  const [view, setView] = useState<"packets" | "airtime">("packets");
  const types = r.packets_per_hour.types;
  const canSwitch = hasAirtimeTypes(types);
  const airtime = canSwitch && view === "airtime";
  const segs = donutSegments(airtime ? airtimeRows(types) : types);
  const c = DONUT.size / 2;
  const legendText = (perHour: number, sharePct: number) => (airtime ? airtimeLegendText(perHour, sharePct) : typeLegendText(perHour, sharePct));
  return (
    <>
      <h4 className="rf-section-h">{airtime ? "PACKET TYPES (seconds on air / hour, every transmission heard in the area)" : "PACKET TYPES (distinct packets / hour)"}</h4>
      {canSwitch && (
        <div className="rf-filter rf-type-switch">
          <HoverTip text={AIRTIME_SWITCH_TIP} placement="above">
            <span>Show </span>
          </HoverTip>
          <div className="theme-toggle" role="group" aria-label="Packet types by">
            <button type="button" data-active={!airtime} onClick={() => setView("packets")}>Packets</button>
            <button type="button" data-active={airtime} onClick={() => setView("airtime")}>Airtime</button>
          </div>
        </div>
      )}
      {segs.length === 0 ? <div>{DASH}</div> : (
    <div className="rf-donut-wrap">
      {/* One overlaid svg per ring segment, each inside a real HoverTip; only the
          painted arc takes the pointer, so hovering a segment names it. */}
      <div className="rf-donut-ring" style={{ width: DONUT.size, height: DONUT.size }} role="img" aria-label={airtime ? "Packet types, share of seconds on air" : "Packet types, share of distinct packets"}>
        {segs.map((sg) => (
          <HoverTip key={sg.type} className="rf-donut-seg" placement="left" text={`${packetTypeName(sg.type)}: ${legendText(sg.perHour, sg.sharePct)}`}>
            <svg width={DONUT.size} height={DONUT.size} viewBox={`0 0 ${DONUT.size} ${DONUT.size}`}>
              <path d={donutArcPath(c, c, DONUT.outer, DONUT.inner, sg.startDeg, sg.endDeg)} fill={sg.color} />
            </svg>
          </HoverTip>
        ))}
      </div>
      <ul className="rf-legend">
        {segs.map((sg) => (
          <li key={sg.type}>
            <span className="rf-swatch" style={{ background: sg.color }} />
            <span className="rf-legend-name">{packetTypeName(sg.type)}</span>
            <span className="rf-legend-val">{legendText(sg.perHour, sg.sharePct)}</span>
          </li>
        ))}
      </ul>
    </div>
      )}
      {airtime && segs.length > 0 && <div className="rf-sub">{advertAirtimeLine(r.packets_per_hour.advert_airtime, r.name, windowKey)}</div>}
    </>
  );
}

// --- one region card ---------------------------------------------------------

function ScopeReachLine({ sc, th, overall }: { sc: RfFloodScope; th: RfThresholds; overall?: boolean }) {
  return (
    <div className="rf-line rf-reach-line" data-overall={overall ? "true" : undefined}>
      <span className="rf-reach-scope" title={sc.name}>{sc.name}</span>
      <span className="rf-group">
        <span className="rf-level" data-level={reachLevel(sc.pct, th) ?? "none"}>
          {fmtPct(sc.pct)}
        </span>
        <span className="rf-sub">{fmtCount(sc.packets)} packets</span>
      </span>
    </div>
  );
}

function FloodReachRow({ r, th }: { r: RfRegion; th: RfThresholds }) {
  const [showScopes, setShowScopes] = useState(false);
  const [showObservers, setShowObservers] = useState(false);
  const v = floodReachView(r.flood_reach);
  const minPackets = th.miss_min_packets ?? DEFAULT_THRESHOLDS.miss_min_packets ?? 30;
  const sortedObservers = sortFloodObservers(v.observers, minPackets);
  const missCell = (o: RfFloodObserver) => {
    const thin = o.expected < minPackets;
    return <span className="rf-level" data-level={thin ? "none" : (o.level ?? "none")}>{fmtPct(o.miss_pct)}</span>;
  };
  return (
    <div className="rf-row" data-testid="flood-reach">
      <div className="rf-row-label">Flood Reach</div>
      <div className="rf-row-value">
        {v.state === "none" && DASH}
        {v.state === "not_enough" && <span className="rf-muted-note" title={`Needs ${r.min_listeners} observers`}>{NOT_ENOUGH_OBSERVERS_LABEL}</span>}
        {v.state === "ok" && (
          <>
            {v.overall && <ScopeReachLine sc={{ name: "All Scopes", scope_kind: "scoped", packets: v.overall.packets, pct: v.overall.pct }} th={th} overall />}
            {v.top.map((sc) => <ScopeReachLine key={sc.name} sc={sc} th={th} />)}
            {showScopes && v.rest.map((sc) => <ScopeReachLine key={sc.name} sc={sc} th={th} />)}
            {v.rest.length > 0 && (
              <button type="button" className="rf-link-btn" aria-expanded={showScopes} onClick={() => setShowScopes(!showScopes)}>
                {showScopes ? `Show Top ${FLOOD_TOP_SCOPES}` : `Show All Scopes (${v.top.length + v.rest.length})`}
              </button>
            )}
            {v.observers.length > 0 && (
              <div className="rf-reach-observers">
                <button type="button" className="rf-link-btn" aria-expanded={showObservers} onClick={() => setShowObservers(!showObservers)}>
                  {showObservers ? "Hide Observers" : `Show Observers (${v.observers.length})`}
                </button>
                {showObservers && (
                  <div className="rf-table-wrap rf-obs-wrap">
                    <table className="node-list-table rf-obs-table" aria-label="Missed By Feeder">
                      <thead>
                        <tr>
                          <th scope="col">Observer</th>
                          <th scope="col" className="rf-num" title="Flood packets this observer should have heard"><span>Expected</span></th>
                          <th scope="col" className="rf-num"><span>Heard</span></th>
                          <th scope="col" className="rf-num" title="Share of expected packets it did not hear"><span>Miss %</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedObservers.map((o) => (
                          <tr key={o.key} data-thin={o.expected < minPackets ? "true" : undefined}>
                            <td><NameCell nodeKey={o.key} name={o.name} linked={o.known !== false} /></td>
                            <td className="rf-num">{fmtCount(o.expected)}</td>
                            <td className="rf-num">{fmtCount(o.heard)}</td>
                            <td className="rf-num">{missCell(o)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <ul className="rf-obs-list" aria-label="Missed By Feeder">
                      {sortedObservers.map((o) => (
                        <li key={o.key} className="rf-obs-item" data-thin={o.expected < minPackets ? "true" : undefined}>
                          <span className="rf-obs-name"><NameCell nodeKey={o.key} name={o.name} linked={o.known !== false} /></span>
                          <span className="rf-obs-miss" title="Share of expected packets it did not hear">{missCell(o)}</span>
                          <span className="rf-obs-counts" title="Flood packets this observer should have heard">{fmtCount(o.heard)} of {fmtCount(o.expected)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CollapsedRows({ r, refs, windowKey, th }: { r: RfRegion; refs: RfReferenceLines; windowKey: RfWindow; th: RfThresholds }) {
  const cu = r.channel_utilization;
  const last = cu.last_hour?.pct ?? null;
  const bl = cu.busiest_listener;
  const bar = utilBarScale(last, cu.peak?.pct ?? null, [refs.pure_aloha, refs.polite_backoff, refs.max_backoff], bl?.pct ?? null);
  const lastLevel = utilLevel(last, th);
  const busiest = r.repeaters.busiest;
  const tr = r.trends;
  const refInfo = refLineInfo(refs);
  const above = noiseAbove(r.noise_floor.observers, th.noise_amber_dbm);
  return (
    <div className="rf-rows">
      <div className="rf-row">
        <div className="rf-row-label">Channel utilization</div>
        <div className="rf-row-value">
          <div className="rf-line">
            <span className="rf-group">
              <span>
                <span className="rf-level" data-level={lastLevel ?? "none"}>
                  {fmtPct(last)}
                </span>{" "}
                last hour
              </span>
              {tr && <TrendTag kind="pts" trend={tr.channel_utilization_avg_pct} windowKey={windowKey} />}
            </span>
            <span className="rf-group">{peakText(cu.peak, windowKey)}</span>
          </div>
          <div className="rf-sub">{busiestObserverText(bl)}</div>
          <div className="rf-bar" role="img" aria-label="Channel utilization against the reference lines">
            <div className="rf-bar-fill" style={{ width: `${bar.fillPct}%`, background: lastLevel ? LEVEL_VAR[lastLevel] : "var(--rf-bar)" }} />
            {bar.ticks.map((t) => (
              <div key={t.value} className="rf-bar-tick" style={{ left: `${t.leftPct}%` }}>
                <HoverTip text={refLineTip(refInfo.find((x) => x.value === t.value) ?? { value: t.value, name: "reference line", cite: "" })} className="rf-bar-tick-hit">
                  <span className="rf-bar-tick-label">{barTickLabel(t.value)}</span>
                </HoverTip>
              </div>
            ))}
            <div className="rf-bar-tick rf-bar-tick-zero" style={{ left: "0%" }}>
              <span className="rf-bar-tick-label">0</span>
            </div>
            {bl && bar.busiestLeftPct !== null && (
              <div className="rf-bar-busiest" style={{ left: `${bar.busiestLeftPct}%` }}>
                <HoverTip text={busiestTip(bl)} className="rf-bar-busiest-hit" placement="above">
                  <span className="rf-sr-only">{busiestTip(bl)}</span>
                </HoverTip>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="rf-row">
        <div className="rf-row-label">Busiest repeater</div>
        <div className="rf-row-value">
          {busiest ? (
            <div className="rf-line">
              <span className="rf-rep-name" title={busiest.name}>{busiest.name}</span>
              <span className="rf-group">
                <span>{repeaterDutyText(busiest)}</span>
                <BasisBadges basis={busiest.basis} external={busiest.external} />
                {tr && <TrendTag kind="pts" trend={tr.busiest_repeater_duty_pct} windowKey={windowKey} />}
              </span>
            </div>
          ) : (
            DASH
          )}
        </div>
      </div>
      <div className="rf-row">
        <div className="rf-row-label">Noise floor</div>
        <div className="rf-row-value">
          {r.noise_floor.typical_dbm === null ? (
            DASH
          ) : (
            <>
              <div className="rf-line">
                <span className="rf-group">
                  <span>
                    <span className="rf-level" data-level={noiseLevel(r.noise_floor.typical_dbm, th) ?? "none"}>
                      {fmtDbm(r.noise_floor.typical_dbm)}
                    </span>{" "}
                    typical
                  </span>
                  {tr && <TrendTag kind="db" trend={tr.noise_typical_dbm} windowKey={windowKey} />}
                </span>
              </div>
              <div className="rf-sub rf-noise-above" data-level={above.n > 0 ? "warn" : "none"}>
                {above.text}
              </div>
            </>
          )}
        </div>
      </div>
      <FloodReachRow r={r} th={th} />
      <div className="rf-row">
        <div className="rf-row-label">Noise Floor Observers</div>
        <div className="rf-row-value">{observerCount(r)}</div>
      </div>
    </div>
  );
}

function ShowAllButton({ total, showAll, onToggle }: { total: number; showAll: boolean; onToggle: () => void }) {
  if (!hasMoreRows(total)) return null;
  return (
    <button type="button" className="rf-link-btn rf-show-all" onClick={onToggle}>
      {showAll ? "Show top 10" : `Show all (${total})`}
    </button>
  );
}

function ExpandedSections({
  r,
  refs,
  windowKey,
  th,
  allNames,
}: {
  r: RfRegion;
  refs: RfReferenceLines;
  windowKey: RfWindow;
  th: RfThresholds;
  allNames: string[];
}) {
  const [allRep, setAllRep] = useState(false);
  const [allNoise, setAllNoise] = useState(false);
  const [allSpill, setAllSpill] = useState(false);
  const reps = topRows(r.repeaters.list, allRep);
  const noiseSorted = useMemo(() => sortNoise(r.noise_floor.observers), [r.noise_floor.observers]);
  const obs = topRows(noiseSorted, allNoise);
  const spillReps = r.unscoped_spill.repeaters ?? [];
  const spillRows = topRows(spillReps, allSpill);
  const chans = channelRows(r.channels);
  const hourLabel = windowKey === "7d" ? localWeekdayHour : localHour;
  const firstHour = r.channel_utilization.series[0]?.hour_start ?? r.channel_utilization.last_hour?.hour_start ?? null;
  const tz = tzAbbrev(firstHour);
  const repeatersTableColumns = [
    { key: "repeater", header: "Repeater", mobileLabel: "Repeater" },
    { key: "average", header: <span className="rf-num">Avg %</span>, mobileLabel: "Average (% of hour)", className: "rf-num" },
    { key: "peak", header: <span className="rf-num">Peak % (hour{tz ? `, ${tz}` : ""})</span>, mobileLabel: `Peak (% of hour${tz ? `, ${tz}` : ""})`, className: "rf-num" },
    { key: "basis", header: "Basis", mobileLabel: "Basis" },
  ];
  const repeaterRows = reps.map((p) => ({
    key: p.key,
    cells: {
      repeater: <NameCell nodeKey={p.key} name={p.name} />,
      average: fmtNum(p.avg_pct),
      peak: `${fmtNum(p.peak_pct)} (${hourLabel(p.peak_hour)})`,
      basis: <BasisBadges basis={p.basis} external={p.external} />,
    },
  }));
  const noiseTableColumns = [
    { key: "observer", header: "Observer", mobileLabel: "Observer" },
    { key: "typical", header: <span className="rf-num">Typical <span className="rf-unit">(dBm)</span></span>, mobileLabel: "Typical (dBm)", className: "rf-num" },
    { key: "latest", header: <span className="rf-num">Latest <span className="rf-unit">(dBm, hour{tz ? `, ${tz}` : ""})</span></span>, mobileLabel: `Latest (dBm, hour${tz ? `, ${tz}` : ""})`, className: "rf-num" },
  ];
  const noiseRows = obs.map((o) => ({
    key: o.key,
    cells: {
      observer: <NameCell nodeKey={o.key} name={o.name} linked={o.known !== false} />,
      typical: <span className="rf-level" data-level={noiseLevel(o.median_dbm, th) ?? "none"}>{fmtNum(o.median_dbm)}</span>,
      latest: <><span className="rf-level" data-level={noiseLevel(o.latest_dbm, th) ?? "none"}>{fmtNum(o.latest_dbm)}</span> ({hourLabel(o.latest_hour)})</>,
    },
  }));
  const spillTableColumns = [
    { key: "repeater", header: "Repeater", mobileLabel: "Repeater" },
    { key: "region", header: "Region", mobileLabel: "Region" },
    { key: "packets", header: <span className="rf-num">Packets relayed <span className="rf-unit">(distinct packets)</span></span>, mobileLabel: "Packets relayed (distinct packets)", className: "rf-num" },
    { key: "share", header: <span className="rf-num">% of the region&apos;s unscoped floods</span>, mobileLabel: "Share of this region's unscoped floods (%)", className: "rf-num" },
  ];
  const spillRowsData = spillRows.map((p) => ({
    key: `${p.key}-${p.region_code}`,
    cells: {
      repeater: <NameCell nodeKey={p.key} name={p.name} />,
      region: <HoverTip text={p.region_name} className="rf-name-tip"><span className="rf-name">{p.region_name}</span></HoverTip>,
      packets: p.packets,
      share: fmtPct(p.pct),
    },
  }));
  const channelTableColumns = [
    { key: "channel", header: "Channel", mobileLabel: "Channel" },
    { key: "messages", header: <span className="rf-num">Msgs / hr</span>, mobileLabel: "Messages per hour", className: "rf-num" },
  ];
  const channelRowsData = chans.map((c) => ({
    key: c.label,
    cells: {
      channel: <NameCell nodeKey="" name={c.label} linked={false} />,
      messages: c.value,
    },
  }));
  return (
    <div className="rf-expanded">
      <h4 className="rf-section-h">CHANNEL UTILIZATION BY HOUR{tz ? ` (${tz})` : ""}</h4>
      <UtilByHourChart series={r.channel_utilization.series} refs={refs} windowKey={windowKey} th={th} />

      <h4 className="rf-section-h">PACKETS / HOUR BY SCOPE (distinct packets)</h4>
      <div className="rf-pre">{packetsLine(r.packets_per_hour.avg)}</div>

      <PacketTypeDonut r={r} windowKey={windowKey} />

      <h4 className="rf-section-h">REPEATER DUTY CYCLE (% of hour transmitting)</h4>
      <ResponsiveRecordTable columns={repeatersTableColumns} rows={repeaterRows} ariaLabel="Repeater duty cycle" />
      {reps.length === 0 && <div className="empty-state">{DASH}</div>}
      <ShowAllButton total={r.repeaters.list.length} showAll={allRep} onToggle={() => setAllRep((v) => !v)} />

      <h4 className="rf-section-h">NOISE FLOOR BY OBSERVER</h4>
      <ResponsiveRecordTable columns={noiseTableColumns} rows={noiseRows} ariaLabel="Noise floor by observer" />
      {obs.length === 0 && <div className="empty-state">{DASH}</div>}
      <ShowAllButton total={r.noise_floor.observers.length} showAll={allNoise} onToggle={() => setAllNoise((v) => !v)} />

      <h4 className="rf-section-h">UNSCOPED SPILL</h4>
      <div>{spillSentence(r.name, r.unscoped_spill, allNames)}</div>
      {spillReps.length > 0 && (
        <>
          <div className="rf-sub rf-spill-title">Repeaters outside {r.name} relaying its unscoped floods</div>
          <ResponsiveRecordTable columns={spillTableColumns} rows={spillRowsData} ariaLabel="Repeaters relaying unscoped floods outside this region" />
          <ShowAllButton total={spillReps.length} showAll={allSpill} onToggle={() => setAllSpill((v) => !v)} />
        </>
      )}

      <h4 className="rf-section-h">CHANNELS</h4>
      {chans.length === 0 ? (
        <div>{NO_CHANNELS_TEXT}</div>
      ) : (
        <ResponsiveRecordTable columns={channelTableColumns} rows={channelRowsData} ariaLabel="Channels" />
      )}
    </div>
  );
}

function RegionCard({
  r,
  refs,
  windowKey,
  th,
  allNames,
  expanded,
  onToggle,
}: {
  r: RfRegion;
  refs: RfReferenceLines;
  windowKey: RfWindow;
  th: RfThresholds;
  allNames: string[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const canToggle = r.enough_listeners;
  return (
    <section className="node-detail-card rf-card" data-region={r.code}>
      <div
        className="rf-card-head"
        data-clickable={canToggle ? "true" : "false"}
        onClick={canToggle ? onToggle : undefined}
      >
        <h3 className="rf-card-title">{regionTitle(r)}</h3>
        {canToggle && (
          // The click bubbles up to the header, which does the toggling: the
          // button is the keyboard-accessible control (Enter/Space click it).
          <button type="button" className="rf-btn rf-btn-outline" aria-expanded={expanded}>
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
      </div>
      <div className="node-detail-card-body">
        {!r.enough_listeners ? <div className="rf-muted-note">{notEnoughObserversText(r)}</div> : <CollapsedRows r={r} refs={refs} windowKey={windowKey} th={th} />}
        {r.enough_listeners && expanded && <ExpandedSections r={r} refs={refs} windowKey={windowKey} th={th} allNames={allNames} />}
      </div>
    </section>
  );
}

// --- page ----------------------------------------------------------------------

export function RfHealthTab() {
  const { isAdmin } = useAuth();
  const [windowKey, setWindowKey] = useState<RfWindow>("24h");
  const [scopeFilter, setScopeFilter] = useState<string>(ALL);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());

  // Stored snapshot, kept in the shared query cache: switching tabs or window
  // never blanks data that is already there.
  const q = useGlobalQuery<RfHealthResponse>(
    ["rf-health", "meshcore", windowKey],
    `/api/rf-health/meshcore?window=${windowKey}`,
    60000,
  );
  const data = q.data;
  const payload = data && !isPending(data) ? data : null;

  const regions = useMemo(
    () => (payload ? filterRegions(payload.regions, scopeFilter) : []),
    [payload, scopeFilter],
  );
  const allNames = useMemo(() => (payload ? payload.regions.map((x) => x.name) : []), [payload]);

  function toggle(code: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  return (
    <div>
      <div className="node-list-toolbar">
        <label className="rf-filter">
          Region{" "}
          <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
            <option value={ALL}>All</option>
            {(payload?.filters.scopes ?? []).map((o) => (
              <option key={o.code} value={o.code}>
                {o.code}
              </option>
            ))}
          </select>
        </label>
        <div className="rf-filter">
          Window{" "}
          <div className="theme-toggle" role="group" aria-label="Window">
            {RF_WINDOWS.map((w) => (
              <button key={w} data-active={windowKey === w} onClick={() => setWindowKey(w)}>
                {w}
              </button>
            ))}
          </div>
        </div>
        {isAdmin && <AddRegionMenu />}
      </div>
      {payload?.airtime_paused && (
        <div className="node-detail-note" data-testid="airtime-paused" role="status">
          {airtimePausedText(payload.airtime_paused.since, formatLocalDateTime)}
        </div>
      )}
      {payload && scopeFilter !== ALL && <div className="rf-sub rf-filter-note">{scopeFilterNote(regions.length, scopeFilter)}</div>}

      {data === null && q.loading && <div className="empty-state">Loading…</div>}
      {data === null && !q.loading && q.error && <div className="node-detail-note">Not available right now.</div>}
      {data && isPending(data) && <div className="node-detail-note">{FIRST_CALCULATION_TEXT}</div>}
      {payload && regions.length === 0 && <div className="empty-state">{scopeFilter === ALL ? DASH : noAreasText(scopeFilter)}</div>}
      <div className="rf-cards">
        {payload &&
          regions.map((r) => (
            <RegionCard
              key={r.code}
              r={r}
              refs={payload.reference_lines}
              windowKey={windowKey}
              th={{ ...DEFAULT_THRESHOLDS, ...(payload.thresholds ?? {}) }}
              allNames={allNames}
              expanded={expanded.has(r.code)}
              onToggle={() => toggle(r.code)}
            />
          ))}
      </div>
    </div>
  );
}
